import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Loader2, AlertCircle } from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_2c2cfe83-306e-4fb5-b370-367ad0387383/artifacts/k0uivc6c_4skr1hVuGATobkW3-light.png";

export default function Login() {
    const navigate = useNavigate();
    const { login } = useAuth();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [formData, setFormData] = useState({
        email: '',
        password: ''
    });

    const handleChange = (e) => {
        setFormData(prev => ({
            ...prev,
            [e.target.name]: e.target.value
        }));
        setError('');
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        try {
            const user = await login(formData.email, formData.password);
            if (user.role === 'admin') {
                navigate('/admin');
            } else {
                navigate('/dashboard');
            }
        } catch (err) {
            setError(err.message || 'Login failed. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-background flex flex-col">
            {/* Header */}
            <header className="p-6">
                <a href="https://geektickets.ie" className="flex items-center gap-2 w-fit">
                    <img src={LOGO_URL} alt="GeekTickets" className="h-10" />
                </a>
            </header>

            {/* Login Form */}
            <main className="flex-1 flex items-center justify-center px-6 pb-12">
                <Card className="w-full max-w-md bg-card border-border animate-slide-up">
                    <CardHeader className="text-center">
                        <CardTitle className="text-2xl font-['Outfit']">Welcome Back</CardTitle>
                        <CardDescription>Sign in to your GeekTickets account</CardDescription>
                    </CardHeader>
                    <CardContent>
                        {error && (
                            <Alert variant="destructive" className="mb-6" data-testid="login-error">
                                <AlertCircle className="h-4 w-4" />
                                <AlertDescription>{error}</AlertDescription>
                            </Alert>
                        )}

                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div className="space-y-2">
                                <Label htmlFor="email">Email</Label>
                                <Input
                                    id="email"
                                    name="email"
                                    type="email"
                                    placeholder="you@example.com"
                                    value={formData.email}
                                    onChange={handleChange}
                                    required
                                    disabled={loading}
                                    className="bg-muted/50"
                                    data-testid="login-email-input"
                                />
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="password">Password</Label>
                                <Input
                                    id="password"
                                    name="password"
                                    type="password"
                                    placeholder="Enter your password"
                                    value={formData.password}
                                    onChange={handleChange}
                                    required
                                    disabled={loading}
                                    className="bg-muted/50"
                                    data-testid="login-password-input"
                                />
                            </div>

                            <Button 
                                type="submit" 
                                className="w-full btn-glow" 
                                disabled={loading}
                                data-testid="login-submit-btn"
                            >
                                {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                                {loading ? 'Signing in...' : 'Sign In'}
                            </Button>
                        </form>

                        <div className="mt-6 text-center text-sm text-muted-foreground">
                            Don't have an account?{' '}
                            <Link to="/register" className="text-primary hover:underline" data-testid="login-register-link">
                                Register here
                            </Link>
                        </div>
                    </CardContent>
                </Card>
            </main>
        </div>
    );
}
